import { toast } from "react-toastify";
import p2pLinkedRfqService from "../services/p2pLinkedRfq.service";

const P2P_LAUNCH_STORAGE_KEY = "b2b_p2p_launch_context";

/**
 * Remember the active P2P transaction while the user works in host modules
 * (e.g. Create Auction) so a second RFQ still links even if URL params are lost.
 */
export function persistP2pLaunchContext(ctx) {
  if (!ctx?.p2p_transaction_id) return;
  try {
    sessionStorage.setItem(
      P2P_LAUNCH_STORAGE_KEY,
      JSON.stringify({
        ...ctx,
        p2p_transaction_id: String(ctx.p2p_transaction_id),
        saved_at: Date.now(),
      }),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function readStoredP2pLaunchContext() {
  try {
    const raw = sessionStorage.getItem(P2P_LAUNCH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.p2p_transaction_id) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearStoredP2pLaunchContext() {
  try {
    sessionStorage.removeItem(P2P_LAUNCH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Return to the P2P transaction Sourcing tab after a host-module RFQ action. */
export function navigateToP2pTransactionSourcing(navigate, transactionId, { tab = "sourcing" } = {}) {
  const txId = transactionId != null ? String(transactionId).trim() : "";
  if (!navigate || !txId) return;
  navigate(`/dashboard/p2p/transactions/${encodeURIComponent(txId)}?tab=${tab}`, {
    replace: true,
  });
}

/**
 * After a host-module RFQ is created, link it to the P2P transaction and return
 * to transaction detail when launched from P2P.
 */
export async function completeP2pRfqLinkAfterModuleCreate({
  searchParams,
  sourceModule,
  sourceRfqId,
  sourceRfqCode,
  sourceRfqTitle,
  sourceRfqStatus,
  sourceUrl,
  navigate,
  p2pLaunchContext,
}) {
  const launch =
    p2pLaunchContext !== undefined
      ? p2pLaunchContext
      : readP2pLaunchContext(searchParams);
  const txId = launch?.p2p_transaction_id;
  if (!txId || !sourceRfqId) {
    return false;
  }

  const finish = (linked) => {
    navigateToP2pTransactionSourcing(navigate, txId);
    return linked;
  };

  try {
    await p2pLinkedRfqService.link(txId, {
      source_module: sourceModule,
      source_rfq_id: String(sourceRfqId),
      source_rfq_code: sourceRfqCode || null,
      source_rfq_title: sourceRfqTitle || null,
      source_rfq_status: sourceRfqStatus || null,
      source_url: sourceUrl || null,
    });
    toast.success("RFQ linked to P2P transaction");
    return finish(true);
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Failed to link RFQ to P2P";
    if (/already linked/i.test(String(msg))) {
      return finish(true);
    }
    toast.error(msg);
    navigateToP2pTransactionSourcing(navigate, txId);
    return false;
  }
}

export function readP2pLaunchContext(searchParams) {
  const fromUrl = readP2pLaunchContextFromUrl(searchParams);
  if (fromUrl) return fromUrl;
  return readStoredP2pLaunchContext();
}

/**
 * P2P launch context from URL query params only — no sessionStorage fallback.
 * Use for standalone module entry points (e.g. /buyer-rfq-form from Dashboard)
 * so a prior P2P session does not prefill or auto-link.
 */
export function readP2pLaunchContextFromUrl(searchParams) {
  const txId = searchParams?.get("p2p_transaction_id");
  if (!txId) return null;
  const sourceContext = searchParams?.get("source_context");
  if (sourceContext && sourceContext !== "p2p") return null;

  const ctx = {
    p2p_transaction_id: txId,
    p2p_organization_id: searchParams.get("p2p_organization_id"),
    return_to: searchParams.get("return_to") || "p2p_transaction_detail",
    source_module: searchParams.get("source_module"),
    category_id: searchParams.get("category_id"),
    category_name: searchParams.get("category_name"),
    estimated_amount: searchParams.get("estimated_amount"),
    product_title: searchParams.get("product_title"),
    quantity: searchParams.get("quantity"),
    currency: searchParams.get("currency"),
  };
  persistP2pLaunchContext(ctx);
  return ctx;
}

/** URL params first, then session storage from the last opened P2P transaction. */
export function getEffectiveP2pLaunchContext(searchParams) {
  return readP2pLaunchContextFromUrl(searchParams) || readStoredP2pLaunchContext();
}
